import { FileText, Image as ImageIcon, LayoutGrid, Type, Video, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** An icon for each record format, so the buttons can be told apart at a glance. */
const FORMAT_ICONS: Record<string, LucideIcon> = {
  image: ImageIcon,
  video: Video,
  pdf: FileText,
  text: Type,
};

interface FormatSwitchProps {
  /** The formats on offer, after "Any". */
  formats: ReadonlyArray<{ slug: string; label: string }>;
  /** The chosen format slug, or null for any format. */
  activeFormat: string | null;
  /** Record count per format slug, given the current category and search. */
  formatCounts?: Record<string, number>;
  /** Records that match the current category and search, whatever their format. */
  formatAllCount?: number;
  /** Switch to a format, or back to any format with null. */
  onSelectFormat: (slug: string | null) => void;
}

/**
 * The record-type switch (Any / Image / Video / Doc / Text) as one rounded,
 * segmented control. The icons are a touch larger than before (18px) so they
 * read at a glance, but they stay beside their words. There is exactly one of
 * these on the page: it lives in the toolbar that sticks under the header.
 */
export function FormatSwitch({
  formats,
  activeFormat,
  formatCounts,
  formatAllCount,
  onSelectFormat,
}: FormatSwitchProps) {
  return (
    <div
      role="group"
      aria-label="Type of record"
      className="grid grid-cols-5 gap-0.5 rounded-full border border-foreground/30 bg-card p-1 shadow-[0_1px_2px_rgba(40,25,10,0.1)]"
    >
      <FormatSegment
        label="Any"
        icon={LayoutGrid}
        count={formatAllCount}
        pressed={activeFormat === null}
        onClick={() => onSelectFormat(null)}
      />
      {formats.map((f) => (
        <FormatSegment
          key={f.slug}
          label={f.label}
          icon={FORMAT_ICONS[f.slug] ?? FileText}
          count={formatCounts?.[f.slug]}
          pressed={activeFormat === f.slug}
          onClick={() => onSelectFormat(activeFormat === f.slug ? null : f.slug)}
        />
      ))}
    </div>
  );
}

function FormatSegment({
  label,
  icon: Icon,
  count,
  pressed,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  count?: number;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(
        'flex h-9 min-w-0 flex-col items-center justify-center gap-0 rounded-full px-1 transition-colors duration-150 motion-reduce:transition-none',
        '[-webkit-tap-highlight-color:transparent] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        pressed
          ? 'bg-foreground text-background shadow-[0_1px_2px_rgba(40,25,10,0.3)]'
          : 'text-foreground hover:bg-foreground/[0.07] active:bg-foreground/[0.12]',
      )}
    >
      <span className="flex items-center gap-1.5">
        <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={pressed ? 2.25 : 2} />
        <span className="truncate font-engraved text-[10px] font-bold uppercase tracking-[0.06em] sm:text-[11px] sm:tracking-[0.12em]">
          {label}
        </span>
      </span>
      {typeof count === 'number' && (
        <span
          className={cn(
            'font-mono text-[9px] leading-none tabular-nums',
            pressed ? 'text-background/75' : 'text-foreground/70',
          )}
        >
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}
